/**
 * region-availability-banner.js — Enrichment custom script (not a Sleek
 * theme file). Drives snippets/region-availability-banner.liquid, loaded
 * sitewide from layout/theme.liquid.
 *
 * WHAT THIS DOES (and, just as importantly, does NOT do)
 * -------------------------------------------------------
 * There is exactly one Shopify Market (United Kingdom, GBP) and one
 * published locale (English) as of 2026-09-23 -- see the Liquid file's own
 * header for the full context. With nothing to switch a visitor TO, this
 * script only ever:
 *   1. Detects the visitor's likely country (client-side, see below).
 *   2. If that country cannot receive a Shopify order at all, shows a
 *      small dismissible notice. NEVER blocks browsing or checkout, NEVER
 *      redirects -- informational only, exactly as the site owner asked
 *      for after rejecting prior geolocation-redirect apps as too
 *      aggressive.
 *   3. Quietly pushes the detected country + navigator.language to the
 *      visitor's Klaviyo profile via identify(), so this data already
 *      exists once a real region-switch flow is built for a second
 *      market. This identify() call fires independently of whether the
 *      banner itself is shown or has been dismissed -- it's data capture,
 *      not a banner side-effect.
 * A future dev building the real switch flow for a second market: this
 * file's job ends at "detect + notify + log". Wire the actual switch into
 * country-selector.liquid / language-selector.liquid once there's a real
 * second market/locale for a visitor to move to.
 *
 * DETECTION METHOD -- WHY THIS ONE
 * ---------------------------------
 * No existing signal in this theme gives a real visitor-geo answer:
 * `localization.country` reflects Market membership (there's only GB in
 * the one Market), not physical location; no existing fetch response in
 * this codebase (cart.js AJAX included) carries a geo/country hint either.
 * A third-party client-side lookup is therefore genuinely necessary.
 * Evaluated https://ipapi.co/json/ (the option this task's brief
 * suggested first) live on 2026-09-23 and it returned HTTP 429
 * ("RateLimited... sign up for a paid plan") on a single cold request --
 * its free tier is too fragile to depend on for a live storefront.
 * Using `https://www.cloudflare.com/cdn-cgi/trace` instead: a long-standing
 * public Cloudflare edge endpoint, free, no API key, no documented request
 * cap, returns plain `key=value` lines including `loc=<ISO-3166-1 alpha-2>`
 * (the country Cloudflare's edge resolved the request to), and confirmed
 * live to send `access-control-allow-origin: *` so a plain fetch() works
 * with no CORS workaround. RISK WORTH FLAGGING: this is a debug/diagnostic
 * endpoint, not a documented, versioned public API -- Cloudflare could
 * change its format or rate-limit it without notice. It's a widely used
 * pattern for exactly this purpose, but if it ever becomes unreliable,
 * swap the URL/parsing in detectCountry() below for a paid geo-IP service
 * instead; every other consumer of countryCode in this file is unaffected.
 * Either way, this call is expressly best-effort: a failed/blocked/slow
 * lookup silently does nothing -- never an error shown to the visitor,
 * never something that can delay or block page rendering (fetch is fired
 * after DOM ready, off the critical path).
 *
 * CONSENT GATING
 * ---------------
 * This store uses Shopify's native "privacy settings automation" (Admin ->
 * Settings -> Customer privacy), the same mechanism already governing the
 * Klaviyo Customer Events pixel on this site (see the 2026-09-06 fix: the
 * pixel was silently sending zero events until this was turned on). BOTH
 * the geo-IP network call and the Klaviyo identify() call below are gated
 * behind `window.Shopify.customerPrivacy.analyticsProcessingAllowed()` --
 * analytics, specifically, since detecting a visitor's region and logging
 * it to a profile is a tracking/telemetry action, not marketing outreach.
 * Neither call fires before that returns true; a visitor who has declined,
 * or simply hasn't answered yet, gets neither. `visitorConsentCollected`
 * is also listened for, so accepting mid-session (without a reload) still
 * triggers detection once, without needing another pageview.
 */
(function () {
  'use strict';

  if (window.Shopify && Shopify.designMode) return; // don't run inside the theme editor

  var STORAGE_KEY = 'enrichment:region-availability-dismissed';
  var DISMISS_EXPIRY_DAYS = 30;
  var CONSENT_POLL_INTERVAL_MS = 500;
  var CONSENT_POLL_MAX_ATTEMPTS = 20; // ~10s -- covers the API attaching slightly after this deferred script runs

  var banner = document.getElementById('RegionAvailabilityBanner');
  var shippableListEl = document.getElementById('RegionAvailabilityShippableCountries');
  if (!banner || !shippableListEl) return;

  var shippableCountries = [];
  try {
    shippableCountries = JSON.parse(shippableListEl.textContent) || [];
  } catch (err) {
    return; // malformed data island -- nothing safe to do, fail silent
  }

  var alreadyActed = false;

  /**
   * Runs analyticsProcessingAllowed() only once it's actually callable, and
   * only invokes `callback` the first time it resolves true. Re-checked on
   * every `visitorConsentCollected` event (covers accepting mid-session)
   * and via a short poll (covers a returning visitor whose consent was
   * already granted on an earlier visit -- no new event fires for a
   * decision that isn't changing).
   */
  function whenAnalyticsConsentGranted(callback) {
    function attempt() {
      if (alreadyActed) return;
      var privacyApi = window.Shopify && window.Shopify.customerPrivacy;
      if (privacyApi && typeof privacyApi.analyticsProcessingAllowed === 'function') {
        if (privacyApi.analyticsProcessingAllowed()) {
          alreadyActed = true;
          callback();
        }
      }
    }

    attempt();
    if (alreadyActed) return;

    document.addEventListener('visitorConsentCollected', attempt);

    var attempts = 0;
    var pollId = window.setInterval(function () {
      attempts += 1;
      attempt();
      if (alreadyActed || attempts >= CONSENT_POLL_MAX_ATTEMPTS) {
        window.clearInterval(pollId);
      }
    }, CONSENT_POLL_INTERVAL_MS);
  }

  /**
   * Best-effort client-side country lookup. See file header for why this
   * endpoint was chosen. Resolves with an uppercase ISO-3166-1 alpha-2
   * code, or an empty string if anything about the lookup failed/was
   * unparseable -- callers must treat '' as "unknown", never as a country.
   */
  function detectCountry() {
    return fetch('https://www.cloudflare.com/cdn-cgi/trace', { credentials: 'omit' })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (text) {
        var match = /(?:^|\n)loc=([A-Za-z]{2})(?:\n|$)/.exec(text || '');
        return match ? match[1].toUpperCase() : '';
      })
      .catch(function () {
        return ''; // network error, blocked request, etc. -- fail silent, no visitor-facing error
      });
  }

  /**
   * Data capture for later -- see file header, point 3. Fires whenever a
   * country was actually detected, regardless of whether it's shippable or
   * whether the banner ends up shown. Uses the exact
   * `window.klaviyo.push([...])` queue convention already established in
   * assets/cart.js and assets/search.js (Klaviyo's onsite-embed app block
   * stubs `window.klaviyo` as a queue array before its real SDK loads, so
   * this is safe regardless of load order).
   */
  function pushKlaviyoIdentify(countryCode) {
    if (!(window.klaviyo && typeof window.klaviyo.push === 'function')) return;
    window.klaviyo.push([
      'identify',
      {
        'Detected Country': countryCode,
        'Detected Language': navigator.language || '',
      },
    ]);
  }

  /**
   * Shows the banner only for a country that (a) was actually detected,
   * (b) is not in the hand-maintained shippable list, and (c) hasn't
   * already been dismissed by this visitor within the last 30 days.
   */
  function maybeShowBanner(countryCode) {
    if (!countryCode || shippableCountries.indexOf(countryCode) !== -1) return;

    if (window.FoxTheme && window.FoxTheme.utils && window.FoxTheme.utils.getStorage(STORAGE_KEY)) {
      return; // dismissed recently -- don't nag a repeat visitor
    }

    banner.hidden = false;

    var dismissBtn = banner.querySelector('[data-region-availability-dismiss]');
    if (dismissBtn) {
      dismissBtn.addEventListener(
        'click',
        function () {
          banner.hidden = true;
          if (window.FoxTheme && window.FoxTheme.utils) {
            window.FoxTheme.utils.setStorage(STORAGE_KEY, { dismissed: true }, DISMISS_EXPIRY_DAYS);
          }
        },
        { once: true }
      );
    }
  }

  function run() {
    // Runs the lookup + identify() unconditionally (subject only to
    // consent, already checked by the caller) -- the identify() call is
    // independent data capture (point 3 in the file header) that should
    // stay fresh on every consenting visit, not just the first one before
    // a dismissal. maybeShowBanner() is the only place the 30-day
    // dismissal storage is consulted, since that's a purely visual
    // "don't nag" concern, not a data-capture one.
    detectCountry().then(function (countryCode) {
      if (!countryCode) return; // nothing usable came back -- do nothing, silently
      pushKlaviyoIdentify(countryCode);
      maybeShowBanner(countryCode);
    });
  }

  window.FoxTheme && window.FoxTheme.DOMready ? window.FoxTheme.DOMready(function () {
    whenAnalyticsConsentGranted(run);
  }) : whenAnalyticsConsentGranted(run);
})();
